import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getTheme } from "@/lib/themes";

export default function ConceptSelector() {
  const { theme } = useParams();
  const navigate = useNavigate();
  const t = getTheme(theme);

  if (!t) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream p-8">
        <div className="text-center">
          <h2 className="headline text-3xl text-ink">Temática no encontrada</h2>
          <Link to="/crear" className="btn-primary mt-6">Volver</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-8 flex items-center justify-between">
        <button onClick={() => navigate("/crear")} className="btn-ghost" data-testid="back-themes-btn">
          <ArrowLeft size={16} /> Cambiar temática
        </button>
        <Link to="/" className="headline text-2xl text-ink">
          Fernebia<span className="script text-warmgold ml-1">·mx</span>
        </Link>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-6 pb-24">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overline mb-6">
          Paso 2 · Elige tu concepto
        </motion.p>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="headline text-5xl md:text-6xl text-ink leading-tight max-w-2xl"
          >
            {t.name}
            <span className="script text-warmgold text-5xl md:text-6xl block mt-1">{t.heroCopy}</span>
          </motion.h1>
          <p className="text-inksoft max-w-sm">
            No son plantillas ni logos. Son 5 conceptos visuales — elige el que te haga suspirar.
          </p>
        </div>

        {/* Editorial bento: 1 big + 2 medium + 2 small */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
          {t.concepts.map((c, i) => {
            const spans = [
              "md:col-span-4 md:row-span-2 aspect-[4/5] md:aspect-auto",
              "md:col-span-2 aspect-[3/4]",
              "md:col-span-2 aspect-[3/4]",
              "md:col-span-3 aspect-[4/3]",
              "md:col-span-3 aspect-[4/3]",
            ];
            return (
              <motion.button
                key={c.id}
                onClick={() => navigate(`/crear/${theme}/${c.id}/editor`)}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ scale: 1.005 }}
                className={`group relative overflow-hidden rounded-3xl bg-sand text-left ${spans[i]}`}
                data-testid={`select-concept-${c.id}`}
              >
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                  <span className="self-start glass rounded-full px-3 py-1 text-xs uppercase tracking-widest text-ink">
                    Concepto 0{i + 1}
                  </span>
                  <div>
                    <h3 className="headline text-3xl md:text-4xl text-white leading-none">{c.name}</h3>
                    <p className="text-white/85 text-sm mt-2 max-w-md">{c.vibe}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-white text-sm border-b border-white/60 pb-0.5 group-hover:border-warmgold group-hover:text-warmgold transition-colors">
                      Personalizar <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
